<template>
  <Sheet v-model="isOpen" size="md" content-class="p-0">
    <template #header>
      <div v-if="title" class="flex items-center gap-3 min-w-0">
        <!-- Small poster -->
        <div class="w-10 overflow-hidden rounded flex-shrink-0">
          <img
            v-if="title.poster_path"
            :src="`https://image.tmdb.org/t/p/w92${title.poster_path}`"
            :alt="displayTitle"
            class="object-cover w-full" />
          <div
            v-else
            class="flex items-center justify-center w-full h-14 bg-gray-700">
            <fa-icon :icon="['far', 'film']" class="text-gray-500 text-xs" />
          </div>
        </div>
        <!-- Title info -->
        <div class="min-w-0 flex-1">
          <h3 class="text-sm font-medium text-white truncate">
            {{ displayTitle }}
          </h3>
          <div class="flex items-center gap-2 text-xs text-gray-400">
            <span>Personal Note</span>
          </div>
        </div>
      </div>
      <span v-else class="text-lg font-semibold text-white">Add Note</span>
    </template>

    <div class="flex flex-col">
      <!-- Error state -->
      <div
        v-if="error"
        class="flex items-center p-3 mx-4 mt-4 text-xs text-red-500 rounded-md bg-red-500/10 border border-red-500/20">
        <fa-icon icon="triangle-exclamation" class="mr-2" />
        <p>{{ error }}</p>
      </div>

      <!-- Note form content -->
      <div class="px-4 pt-4 pb-4">
        <form
          id="standalone-note-form"
          class="space-y-4"
          @submit.prevent="submitNote">
          <div>
            <label
              class="block mb-2 text-xs font-medium text-gray-400 uppercase tracking-wide">
              Your Note
            </label>
            <textarea
              v-model="noteContent"
              class="w-full p-3 text-sm text-white rounded-lg border border-gray-700 bg-gray-800 resize-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
              rows="5"
              placeholder="Add your thoughts about this title..."></textarea>
            <p class="mt-1 text-xs text-gray-500">
              This note is private and only visible to you.
            </p>
          </div>
        </form>
      </div>
    </div>

    <template #footer>
      <!-- Remove note option with confirmation -->
      <div v-if="hasExistingNote" class="mb-3">
        <button
          v-if="!confirmingDelete"
          type="button"
          class="w-full py-2 text-xs text-white/40 hover:text-red-400 transition-colors"
          @click="confirmingDelete = true">
          Remove Note
        </button>
        <div v-else class="flex items-center justify-between gap-3 px-1">
          <span class="text-xs text-red-400">Remove this note?</span>
          <div class="flex gap-2">
            <button
              type="button"
              class="px-3 py-1.5 text-xs text-gray-400 hover:text-white transition-colors"
              @click="confirmingDelete = false">
              Keep
            </button>
            <button
              type="button"
              class="px-3 py-1.5 text-xs text-red-400 hover:text-red-300 font-medium transition-colors"
              @click="deleteNote">
              Remove
            </button>
          </div>
        </div>
      </div>

      <!-- Action buttons -->
      <div class="flex gap-2">
        <button
          type="button"
          class="flex-1 py-3 text-sm font-medium text-gray-300 bg-gray-800 rounded-lg hover:bg-gray-700 transition-colors"
          @click="close">
          Cancel
        </button>
        <button
          type="submit"
          form="standalone-note-form"
          class="flex-[2] py-3 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          :disabled="loading">
          {{
            loading ? 'Saving...' : hasExistingNote ? 'Save Note' : 'Add Note'
          }}
        </button>
      </div>
    </template>
  </Sheet>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { useStoreTitlesUser } from '@/stores/storeTitlesUser'
import { Sheet } from '@/components/Options'

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false
  },
  title: {
    type: Object,
    default: null
  },
  titleType: {
    type: String,
    default: 'movie'
  },
  initialNote: {
    type: String,
    default: ''
  }
})

const emit = defineEmits(['update:modelValue', 'saved'])

const storeTitlesUser = useStoreTitlesUser()

const loading = ref(false)
const error = ref('')
const noteContent = ref('')
const confirmingDelete = ref(false)

const isOpen = computed({
  get: () => props.modelValue,
  set: (value) => {
    emit('update:modelValue', value)
  }
})

// Title display
const displayTitle = computed(() => {
  return props.title?.title || props.title?.name || 'Unknown Title'
})

// Check if there's an existing note
const hasExistingNote = computed(() => {
  return !!props.initialNote
})

// Initialize content when sheet opens
watch(
  isOpen,
  (open) => {
    if (open) {
      noteContent.value = props.initialNote || ''
      error.value = ''
      confirmingDelete.value = false
    }
  },
  { immediate: true }
)

async function submitNote() {
  loading.value = true
  error.value = ''

  try {
    const titleData = {
      titleId: props.title?.id,
      titleType: props.title?.media_type || props.titleType
    }

    const noteText = noteContent.value.trim() || null

    const result = await storeTitlesUser.updateNotes(titleData, noteText)

    if (!result.success) {
      throw new Error(result.error || 'Failed to save note')
    }

    emit('saved', noteText)
    close()
  } catch (err) {
    error.value = err.message || 'Failed to save note'
  } finally {
    loading.value = false
  }
}

async function deleteNote() {
  noteContent.value = ''
  await submitNote()
}

function close() {
  isOpen.value = false
}
</script>
